// @spec SUP-STR-017
'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DEFAULT_TEMPLATE_FILTERS,
  QuestionnaireTemplateList,
  useArchiveTemplate,
  useDuplicateTemplate,
  useQuestionnaireTemplates,
  useRestoreTemplate,
} from '@/features/suppliers/admin/questionnaire-templates';
import type {
  QuestionnaireTemplateListItem,
  TemplateFilters,
} from '@/features/suppliers/admin/questionnaire-templates';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/suppliers/admin/questionnaire-templates/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.questionnaireTemplates'),
  }),
  component: QuestionnaireTemplatesIndexPage,
});

/**
 * Questionnaire Templates listing page.
 *
 * Displays all questionnaire templates for the current organization with:
 * - Search and filtering by category and status
 * - Table layout with template details (sections, questions, scoring)
 * - Row actions (archive, restore, duplicate)
 */
function QuestionnaireTemplatesIndexPage() {
  const { t } = useTranslation(['suppliers', 'common']);
  const navigate = useNavigate();
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);

  // Filter state
  const [filters, setFilters] = React.useState<TemplateFilters>(DEFAULT_TEMPLATE_FILTERS);

  // Duplicate dialog state
  const [duplicateDialog, setDuplicateDialog] = React.useState<{
    open: boolean;
    template: QuestionnaireTemplateListItem | null;
    name: string;
  }>({ open: false, template: null, name: '' });

  // Data fetching
  const { data, isLoading: templatesLoading } = useQuestionnaireTemplates({
    page: 1,
    pageSize: 50,
    ...filters,
    enabled: Boolean(activeOrg?.id),
  });

  // Mutations
  const archiveTemplate = useArchiveTemplate({
    onSuccess: () => toast.success(t('suppliers:toast.templateArchived')),
    onError: (error) => toast.error(error.message || i18n.t('suppliers:toast.failedToArchiveTemplate')),
  });

  const restoreTemplate = useRestoreTemplate({
    onSuccess: () => toast.success(t('suppliers:toast.templateRestored')),
    onError: (error) => toast.error(error.message || i18n.t('suppliers:toast.failedToRestoreTemplate')),
  });

  const duplicateTemplate = useDuplicateTemplate({
    onSuccess: (data) => {
      toast.success(t('suppliers:toast.templateDuplicated'));
      setDuplicateDialog({ open: false, template: null, name: '' });
      void navigate({
        to: '/suppliers/admin/questionnaire-templates/$templateId',
        params: { templateId: data.id },
      });
    },
    onError: (error) => toast.error(error.message || i18n.t('suppliers:toast.failedToDuplicateTemplate')),
  });

  // Handlers
  const handleRowClick = React.useCallback(
    (templateId: string) => {
      void navigate({
        to: '/suppliers/admin/questionnaire-templates/$templateId',
        params: { templateId },
      });
    },
    [navigate]
  );

  const handleArchive = React.useCallback(
    (template: QuestionnaireTemplateListItem) => {
      archiveTemplate.mutate(template.id);
    },
    [archiveTemplate]
  );

  const handleRestore = React.useCallback(
    (template: QuestionnaireTemplateListItem) => {
      restoreTemplate.mutate(template.id);
    },
    [restoreTemplate]
  );

  const handleDuplicateClick = React.useCallback((template: QuestionnaireTemplateListItem) => {
    setDuplicateDialog({
      open: true,
      template,
      name: `${template.name} (Copy)`,
    });
  }, []);

  const handleDuplicateSubmit = React.useCallback(() => {
    if (duplicateDialog.template && duplicateDialog.name.trim()) {
      duplicateTemplate.mutate({
        templateId: duplicateDialog.template.id,
        name: duplicateDialog.name.trim(),
      });
    }
  }, [duplicateDialog, duplicateTemplate]);

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('common:routeErrors.noOrgSelectedDesc')}
            </p>
            <Button variant="outline" asChild className="mt-4">
              <Link to="/settings/organizations">{t('common:routeErrors.goToOrganizations')}</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          {/* Header */}
          <header className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Questionnaire Templates</h1>
              <p className="mt-1 text-muted-foreground">
                Create and manage assessment questionnaires for supplier due diligence
              </p>
            </div>
            <Button asChild>
              <Link to="/suppliers/admin/questionnaire-templates/new">
                <Plus className="mr-2 h-4 w-4" />
                Create Template
              </Link>
            </Button>
          </header>

          {/* Questionnaire Templates List */}
          {templatesLoading ? (
            <div className="space-y-4">
              <div className="flex gap-4">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-10 w-32" />
              </div>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : (
            <QuestionnaireTemplateList
              templates={data?.items ?? []}
              filters={filters}
              onFiltersChange={setFilters}
              onRowClick={handleRowClick}
              onArchiveClick={handleArchive}
              onRestoreClick={handleRestore}
              onDuplicateClick={handleDuplicateClick}
              isAdmin
            />
          )}
        </div>
      </div>

      {/* Duplicate Dialog */}
      <Dialog
        open={duplicateDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDuplicateDialog({ open: false, template: null, name: '' });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Template</DialogTitle>
            <DialogDescription>
              Create a copy of &quot;{duplicateDialog.template?.name}&quot; with a new name.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="duplicate-name">New Template Name</Label>
            <Input
              id="duplicate-name"
              value={duplicateDialog.name}
              onChange={(e) => {
                setDuplicateDialog((prev) => ({ ...prev, name: e.target.value }));
              }}
              className="mt-2"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDuplicateDialog({ open: false, template: null, name: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDuplicateSubmit}
              disabled={!duplicateDialog.name.trim() || duplicateTemplate.isPending}
            >
              {duplicateTemplate.isPending ? 'Duplicating...' : 'Duplicate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
